// =============================================================================
// AttachUserModal.jsx — searchable "Attach existing user" picker (Phase 3)
// =============================================================================
// Replaces the first-cut flat <select> on the shelter detail's Users tab
// (John, 5 July 2026: a select doesn't scale to hundreds of caseworkers).
// Client-side search over the already-loaded caseworker profiles (name/email),
// showing each candidate's current shelter codes so an admin can tell
// same-named users apart. Display is capped — refine the search rather than
// scroll hundreds of rows.

import React from "react";
import { Btn } from "../../utils/ui.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";

const MAX_SHOWN = 30;

// THE WALL (docs/staff-caseworker-separation-plan.md): these candidates are DFV
// Service caseworkers, so their name and email come from their membership CONTACT
// (first_name / last_name / email), NOT from profiles.staff_*, which is Zephyr
// staff only. The caller enriches each candidate from their memberships;
// display_name is the fallback for anyone with no contact row yet.
function candidateName(u) {
  return [u.first_name, u.last_name].filter(Boolean).join(" ") || u.display_name || u.email || u.id;
}

function matches(u, q) {
  if (!q) return true;
  const hay =
    `${u.first_name || ""} ${u.last_name || ""} ${u.display_name || ""} ${u.email || ""}`.toLowerCase();
  return hay.includes(q);
}

export function AttachUserModal({
  open,
  candidates, // active caseworker profiles NOT yet attached to this shelter
  shelterName,
  onAttach, // (userId) => void
  onClose,
}) {
  const [search, setSearch] = React.useState("");
  // Reset the search each time the modal opens.
  React.useEffect(() => {
    if (open) setSearch("");
  }, [open]);
  if (!open) return null;

  const q = search.toLowerCase().trim();
  const filtered = (candidates || []).filter(function (u) {
    return matches(u, q);
  });
  const shown = filtered.slice(0, MAX_SHOWN);

  return (
    <ModalBackdrop onClose={onClose} portal>
      <div
        style={{
          background: "#FFFFFF",
          borderRadius: 14,
          width: "calc(100vw - 40px)",
          maxWidth: 640,
          boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
          padding: 24,
        }}
      >
        <div className="font-bold text-[16px] text-zephyr-teal-dark mb-1">Attach existing user</div>
        <div className="text-[13px] text-zephyr-muted mb-3">
          Give an existing caseworker access to {shelterName || "this DFV Service"} (multi-service
          access). New people are created with <b>+ New User</b> instead.
        </div>
        <input
          // biome-ignore lint/a11y/noAutofocus: search-first modal — focusing the search box is the whole point
          autoFocus
          placeholder="Search by name or email..."
          aria-label="Search users to attach"
          value={search}
          onChange={function (e) {
            setSearch(e.target.value);
          }}
          className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] mb-3"
          style={{ fontFamily: "inherit" }}
        />
        {(candidates || []).length === 0 ? (
          <div className="text-[13px] text-zephyr-muted italic py-4">
            Every active caseworker is already attached to this DFV Service.
          </div>
        ) : shown.length === 0 ? (
          <div className="text-[13px] text-zephyr-muted italic py-4">
            No users match "{search}".
          </div>
        ) : (
          <div
            className="border border-zephyr-border rounded-[8px] overflow-y-auto"
            style={{ maxHeight: "50vh" }}
          >
            {shown.map(function (u, idx) {
              return (
                <div
                  key={u.id}
                  className="flex items-center gap-3 py-[10px] px-[14px] text-[13px]"
                  style={{
                    borderTop: idx > 0 ? "1px solid #EEF3F2" : "none",
                    background: idx % 2 === 0 ? "#FFFFFF" : "#F7F9F9",
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{candidateName(u)}</div>
                    <div className="text-zephyr-muted text-[12px] overflow-hidden text-ellipsis whitespace-nowrap">
                      {/* The membership CONTACT email — not profiles.staff_*. */}
                      {u.email || "—"}
                    </div>
                  </div>
                  <div className="flex gap-[4px] flex-wrap justify-end">
                    {(u.shelter_codes || []).map(function (code) {
                      return (
                        <span
                          key={code}
                          className="text-[10px] font-bold rounded-[3px] py-[1px] px-[6px] bg-zephyr-teal-light text-zephyr-teal-dark"
                        >
                          {code}
                        </span>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={function () {
                      onAttach(u.id);
                    }}
                    className="bg-zephyr-teal text-white border-0 rounded-[7px] py-[6px] px-[14px] text-[12px] cursor-pointer font-semibold shrink-0"
                    style={{ fontFamily: "inherit" }}
                  >
                    Attach
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {filtered.length > MAX_SHOWN && (
          <div className="text-[12px] text-zephyr-muted mt-2">
            Showing the first {MAX_SHOWN} of {filtered.length} matches — refine the search to narrow
            it down.
          </div>
        )}
        <div className="flex justify-end mt-4">
          <Btn variant="secondary" onClick={onClose}>
            Close
          </Btn>
        </div>
      </div>
    </ModalBackdrop>
  );
}
